import { bool, object } from 'prop-types';
import get from 'lodash.get';
import React from 'react';
import { createRefetchContainer, graphql } from 'react-relay';
import { defineMessages, intlShape, injectIntl } from 'react-intl';
import { trackEvent } from 'dibs-tracking';
import { localStorage } from 'dibs-browser-storage';
import SearchBarOverlay from './SearchBarOverlay';
import SearchButton from './SearchButton';
import ClearButton from './ClearButton';
import { dedupeLocalStorage, getCursorTerms } from '../../utils/sharedSearchBarHelpers';
import {
    KEY_DOWN_VALUE,
    KEY_UP_VALUE,
    RECENT_SEARCHES,
    SEARCH_PAGE_PATH,
    SEARCH_QUERY_PARAM,
    TRACKING,
} from '../../utils/sharedSearchBarConstants';
import debounce from '../../utils/debounce';
import qs from 'querystring';
import classnames from 'classnames';

import styles from './SearchBar-style.scss';

const messages = defineMessages({
    placeholder: {
        id: 'dbl.Header.SearchBar.placeholder',
        defaultMessage: 'Search for items, creators, or styles',
    },
});

export class SearchBar extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            value: '',
            focused: false,
            hovered: false,
            cursor: {
                terms: [],
                position: -1,
                highlightId: undefined,
            },
        };

        this.handleChange = this.handleChange.bind(this);
        this.handleSubmit = this.handleSubmit.bind(this);
        this.handleCursorChange = this.handleCursorChange.bind(this);
        this.handleKeyDown = this.handleKeyDown.bind(this);
        this.handleRowClick = this.handleRowClick.bind(this);
        this.handleClearSearch = this.handleClearSearch.bind(this);
        this.fetchSuggestions = debounce(this.fetchSuggestions.bind(this), 250);

        this.onFocus = this.onFocus.bind(this);
        this.onBlur = this.onBlur.bind(this);
        this.onMouseOver = this.onMouseOver.bind(this);
        this.onMouseOut = this.onMouseOut.bind(this);
    }

    componentDidMount() {
        this.input.addEventListener('focus', this.onFocus);
        this.input.addEventListener('blur', this.onBlur);
        this.overlayWrapper.addEventListener('mouseover', this.onMouseOver);
        this.overlayWrapper.addEventListener('mouseout', this.onMouseOut);
        this.setSearchTermFromParams();
    }

    componentDidUpdate(prevProps) {
        if (this.props.viewer.searchSuggestions && !prevProps.viewer.searchSuggestions) {
            trackEvent({
                category: TRACKING.CATEGORY,
                action: 'search suggestion displayed',
                label: 'none',
            });
        }
    }

    componentWillUnmount() {
        this.input.removeEventListener('focus', this.onFocus);
        this.input.removeEventListener('blur', this.onBlur);
        this.overlayWrapper.removeEventListener('mouseover', this.onMouseOver);
        this.overlayWrapper.removeEventListener('mouseout', this.onMouseOut);
    }

    setSearchTermFromParams() {
        const queryString = location.search ? location.search.split('?')[1] : '';
        const searchTermFromParams = decodeURIComponent(qs.parse(queryString)[SEARCH_QUERY_PARAM]);
        if (location.href.indexOf(SEARCH_PAGE_PATH) !== -1 && searchTermFromParams) {
            this.setState({ value: searchTermFromParams });
        }
    }

    onFocus() {
        this.setState({ focused: true });
    }

    onBlur() {
        this.setState({ focused: false });
    }

    onMouseOver() {
        this.setState({ hovered: true });
    }

    onMouseOut() {
        this.setState({ hovered: false });
    }

    handleRowClick(value, href) {
        const label = this.props.viewer.searchSuggestions
            ? 'global search|auto-complete selected'
            : 'recent search';
        this.input.value = value;
        this.setState({ value, focused: false, hovered: false });

        trackEvent({
            category: TRACKING.CATEGORY,
            action: TRACKING.ACTION,
            label,
        });

        location.assign(href);
    }

    handleCursorChange(move) {
        const cursor = Object.assign({}, this.state.cursor);
        const searchSuggestions = this.props.viewer.searchSuggestions || {};
        const { suggestions } = searchSuggestions;
        const recentSearches = dedupeLocalStorage();
        const dropdownChoices = suggestions || recentSearches;
        let value = '';

        if (!dropdownChoices) {
            // if we have no section values exit out
            return;
        }

        if (!cursor.terms.length) {
            cursor.terms = getCursorTerms(dropdownChoices, this.input.value, this.props.intl);
        }

        switch (move) {
            case KEY_UP_VALUE:
                cursor.position =
                    cursor.position || cursor.position === 0
                        ? (cursor.position - 1 + cursor.terms.length) % cursor.terms.length
                        : cursor.terms.length - 2;
                break;
            case KEY_DOWN_VALUE:
                cursor.position =
                    cursor.position || cursor.position === 0
                        ? (cursor.position + 1) % cursor.terms.length
                        : 0;
                break;
            default:
                break;
        }

        value = cursor.terms[cursor.position].term;
        cursor.highlightId = cursor.terms[cursor.position].highlightId;

        this.setState({ value, cursor });
    }

    handleKeyDown(event) {
        if (event.keyCode === 27) {
            this.input.blur();
            this.setState({ visible: false });
        }

        if (event.keyCode === 40) {
            event.preventDefault();
            this.handleCursorChange(KEY_DOWN_VALUE);
        }

        if (event.keyCode === 38) {
            event.preventDefault();
            this.handleCursorChange(KEY_UP_VALUE);
        }
    }

    handleChange(event) {
        const value = event.target.value || '';
        const cursor = { terms: [], position: -1 };

        this.setState({ value, cursor }, this.fetchSuggestions);
    }

    handleClearSearch() {
        this.setState({ value: '', cursor: { terms: [], position: -1 } });
    }

    fetchSuggestions() {
        const { value } = this.state;
        const { hasRootPendingRefetch, viewer } = this.props;
        const isSuggestionsFeatureEnabled = get(viewer, 'isSuggestionsFeatureEnabled');
        if (!hasRootPendingRefetch && isSuggestionsFeatureEnabled) {
            this.props.relay.refetch(
                () => ({
                    hasQuery: value.length > 1,
                    query: value.toLowerCase(),
                }),
                null
            );
        }
    }

    handleSubmit(event) {
        event.preventDefault();
        const { value: term, cursor } = this.state;
        const { position, terms } = cursor;
        let urlLabel;

        if (position > -1 && terms[position].link) {
            this.handleRowClick(terms[position].term, terms[position].link);
        } else if (term) {
            urlLabel = `${location.origin}/search/?q=${encodeURIComponent(term.toLowerCase())}`;
            trackEvent({
                category: TRACKING.CATEGORY,
                action: TRACKING.ACTION,
                label: 'global search',
            });
            this.handleRecentStorage(term, urlLabel);
            location.assign(urlLabel);
        }
    }

    handleRecentStorage(term, urlLabel) {
        const search = { term, urlLabel };
        let recentSearches = localStorage.getItem(RECENT_SEARCHES.LOCAL_STORAGE) || [];
        let recentTerms = [];

        if (!recentSearches.length) {
            recentSearches = [
                {
                    type: RECENT_SEARCHES.TYPE,
                    terms: [search],
                },
            ];
        } else {
            recentTerms = recentSearches[0].terms;
            if (recentTerms.length === RECENT_SEARCHES.MAX_TERMS) {
                recentTerms.pop();
            }
            recentTerms.unshift(search);
        }

        localStorage.setItem(RECENT_SEARCHES.LOCAL_STORAGE, JSON.stringify(recentSearches));
    }

    render() {
        const { intl, isClient, viewer } = this.props;
        const { value } = this.state;
        const cursorHighlightId = this.state.cursor.highlightId;
        const { suggestions } = viewer.searchSuggestions || {};
        const recentSearches = dedupeLocalStorage() || [];
        const visible = this.state.focused || this.state.hovered;

        const inputClasses = classnames(styles.input, {
            [styles.hasTerm]: !!value,
        });

        return (
            <div className={styles.wrapper}>
                <form className={styles.form} action="/search/" onSubmit={this.handleSubmit}>
                    <input
                        ref={input => {
                            this.input = input;
                        }}
                        autoCorrect="off"
                        autoCapitalize="off"
                        autoComplete="off"
                        spellCheck="false"
                        className={inputClasses}
                        type="text"
                        name="q"
                        placeholder={intl.formatMessage(messages.placeholder)}
                        value={value}
                        onChange={this.handleChange}
                        onKeyDown={this.handleKeyDown}
                        data-tn="global-header-search-input"
                    />
                    {value && <ClearButton onClick={this.handleClearSearch} />}
                    <SearchButton hasSearchTerm={!!value} />
                </form>
                <div
                    className={styles.overlayWrapper}
                    ref={overlayWrapper => {
                        this.overlayWrapper = overlayWrapper;
                    }}
                >
                    {isClient ? (
                        <SearchBarOverlay
                            handleRowClick={this.handleRowClick}
                            visible={visible}
                            suggestions={suggestions || recentSearches}
                            cursorHighlightId={cursorHighlightId}
                        />
                    ) : null}
                </div>
            </div>
        );
    }
}

SearchBar.propTypes = {
    hasRootPendingRefetch: bool,
    isClient: bool,
    intl: intlShape,
    relay: object,
    viewer: object,
};

export default createRefetchContainer(
    injectIntl(SearchBar),
    {
        viewer: graphql`
            fragment SearchBar_viewer on Viewer
                @argumentDefinitions(
                    query: { type: "String!", defaultValue: "" }
                    hasQuery: { type: "Boolean!", defaultValue: false }
                ) {
                isSuggestionsFeatureEnabled: isEnabledByLocale(
                    featureName: "SEARCH_BAR_SUGGESTIONS"
                )
                searchSuggestions(query: $query, includeTypes: ["POPULAR", "CATEGORY", "SELLER"])
                    @include(if: $hasQuery) {
                    suggestions {
                        type
                        terms {
                            term
                            urlLabel
                        }
                    }
                }
            }
        `,
    },
    graphql`
        query SearchBarRefetchQuery($query: String!, $hasQuery: Boolean!) {
            viewer {
                ...SearchBar_viewer @arguments(query: $query, hasQuery: $hasQuery)
            }
        }
    `
);
